<template>
  <div v-if="viewRows.length" class="linesCard surface-card">
    <span class="linesEyebrow">Phone lines</span>
    <div class="linesGrid" :class="{ callableGrid: callable }">
      <div v-for="r in viewRows" :key="r.n.entry_id" class="lineRow">
        <span v-if="callable" class="lineLead">
          <button
            class="callBtn"
            :disabled="r.n.dialability === 'blocked_no_local_number'"
            :title="r.n.dialability === 'blocked_no_local_number' ? callsUnavailableMessage(r.n.country) : `Call ${prettyFor(r.n)}`"
            :aria-label="`Call ${typeLabelFor(r.n)} ${prettyFor(r.n)}`"
            @click="emit('call', r.n)"
          >
            <AppIcon name="phone_call" :size="12" />
          </button>
        </span>
        <span class="lineType">{{ typeLabelFor(r.n) }}</span>
        <span class="lineNum" :class="{ lineNumOn: r.live }">{{ prettyFor(r.n) }}</span>
        <span v-if="r.live" class="lineLive" :class="{ on: connected, degraded: !!shownFault }">
          <span class="pulse"></span>
          <span>{{ liveText }}</span>
          <span v-if="machineDetected" class="machineTag">
            <AppIcon name="voicemail" :size="11" />Machine
          </span>
          <span v-if="fromPretty" class="liveFrom">from {{ fromPretty }}</span>
        </span>
        <span v-else class="lineStatus" :class="{ done: r.status.tone === 'done' }" :title="r.status.title">{{ r.status.text }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import { callsUnavailableMessage } from './dialability'
import { relativeAgo } from '../../utils/localTime'
import { formatPhone, phoneTypeLabel } from '../../utils/phone'
import type { CallerQueueEntry, QueuedNumber } from '../../../services/dialerApi'
import type { SessionLine } from '../../composables/useDialerSession'
import { dispositionDisplayLabel } from './dialerDispositions'
import { MEDIA_FAULT_LABELS } from './mediaFaults'
import type { MediaFault } from '../../../composables/useDialer'

// The panel's call surface — every line the person can be reached on, with its
// dial history, a per-line Call button pre-dial, and the live call state ON the
// row being dialled once a call is out (the state changes where the rep is
// already looking, not in a far corner).
const props = defineProps<{
  entry: CallerQueueEntry | null
  /** Session lines with logged outcomes (during a dialing session). */
  sessionLines?: readonly SessionLine[]
  /** e164 of the line being dialled right now (claim.prospect_e164). */
  activeE164?: string
  ringing?: boolean
  connected?: boolean
  durationSeconds?: number
  fromE164?: string | null
  /** AMD reported a machine on the line being dialled. */
  machineDetected?: boolean
  /** The call ended on its own; the session is reading Twilio's verdict on the line. */
  checkingLine?: boolean
  /** Pre-dial: render a per-row Call button (starts a one-off on that line). */
  callable?: boolean
  /** Live media health on the dialed row — amber-flags the pill. */
  mediaFault?: MediaFault | null
}>()

const emit = defineEmits<{ (e: 'call', line: QueuedNumber): void }>()

// A legacy payload can lack numbers[]; the person-level fields describe its one
// line (same fallback shape the session uses — keep them agreeing).
const rows = computed<QueuedNumber[]>(() => {
  const e = props.entry
  if (!e) return []
  if (e.numbers?.length) return e.numbers
  return [{
    entry_id: e.id,
    e164: e.prospect_e164,
    phone_type: null,
    dialability: e.dialability,
    cooldown_until: e.cooldown_until,
    country: e.country,
    timezone: e.timezone,
    last_connect: e.last_connect,
  }]
})

const viewRows = computed(() =>
  rows.value.map((n) => ({
    n,
    live: props.activeE164 === n.e164,
    status: lineStatus(n),
  })),
)

function typeLabelFor(n: QueuedNumber): string {
  return phoneTypeLabel(n.phone_type)
}
function prettyFor(n: QueuedNumber): string {
  return formatPhone(n.e164)
}

// "Last connect" only when someone actually picked up; otherwise the attempt's
// own wording — the vocabulary reps know from the queue and history pages.
const CONNECTED_OUTCOMES = new Set([
  'human_picked_up', 'answered', 'connected_interested', 'connected_callback',
  'connected_not_interested', 'gatekeeper', 'do_not_call', 'wrong_number',
])
const ATTEMPT_OUTCOME_LABELS: Record<string, string> = {
  voicemail_left: 'voicemail',
  no_answer: 'no answer',
  busy: 'busy',
  failed: 'call failed',
}

function lastDialStatus(lc: QueuedNumber['last_connect']): string | null {
  if (!lc?.at) return null
  const age = relativeAgo(lc.at)
  const outcome = String(lc.outcome || '')
  if (CONNECTED_OUTCOMES.has(outcome)) return `Last connect · ${age}`
  const ol = ATTEMPT_OUTCOME_LABELS[outcome]
  return ol ? `${ol[0].toUpperCase()}${ol.slice(1)} · ${age}` : `Last dialed · ${age}`
}

function lineStatus(n: QueuedNumber): { text: string; tone: 'done' | 'idle'; title?: string } {
  // Most specific first: what this session logged on the line, then its dial
  // history, then why it can't be dialed at all.
  const code = props.sessionLines?.find((l) => l.num.e164 === n.e164)?.outcome
  if (code) return { text: dispositionDisplayLabel(code), tone: 'done' }
  const last = lastDialStatus(n.last_connect)
  if (last) return { text: last, tone: 'idle' }
  if (n.dialability === 'blocked_no_local_number') {
    return { text: 'Can’t be dialed', tone: 'idle', title: callsUnavailableMessage(n.country) }
  }
  return { text: 'Never dialed', tone: 'idle' }
}

// The timer only runs once someone answers, so "Connected" is never shown at 0:00.
const durationLabel = computed(() => {
  const s = Math.max(0, props.durationSeconds ?? 0)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
})
const shownFault = computed(() => (props.connected ? (props.mediaFault ?? null) : null))
const liveText = computed(() => {
  // The fault replaces the label but keeps the clock: the call is still up, and
  // the rep needs to see how long it has been burning.
  if (shownFault.value) return `${MEDIA_FAULT_LABELS[shownFault.value]} ${durationLabel.value}`
  if (props.connected) return `Connected ${durationLabel.value}`
  if (props.checkingLine) return 'Checking the line…'
  return props.ringing ? 'Ringing…' : 'Calling…'
})
const fromPretty = computed(() => (props.fromE164 ? formatPhone(props.fromE164) : ''))
</script>

<style scoped>
.linesCard { padding: var(--space-4) var(--space-5); }
.linesEyebrow { display: block; margin-bottom: var(--space-3); font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
/* [call] | type | number | status — intrinsic columns keep every row aligned
   without per-cell min-width tuning, packed LEFT so everything about a line
   sits together. The call icon LEADS the row: one constant aim point per row,
   a faint ghost at rest that fills green on hover/focus — quiet without being
   hidden (touch devices and first-time reps still see it). */
.linesGrid { display: grid; grid-template-columns: max-content max-content minmax(0, max-content); justify-content: start; column-gap: var(--space-4); row-gap: var(--space-2); align-items: center; }
.linesGrid.callableGrid { grid-template-columns: max-content max-content max-content minmax(0, max-content); }
/* A real box via subgrid (not display:contents) so :hover holds across the
   column gaps too — the row owns its whole strip while the parent grid keeps
   every column aligned. */
.lineRow { grid-column: 1 / -1; display: grid; grid-template-columns: subgrid; column-gap: var(--space-4); align-items: center; }
.lineLead { display: inline-flex; }
.callBtn { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: var(--radius-full); border: 0; padding: 0; background: transparent; color: var(--color-accent); opacity: 0.45; cursor: pointer; transition: opacity var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out); }
.lineRow:hover .callBtn:not(:disabled), .callBtn:focus-visible { opacity: 1; background: var(--color-accent); color: #fff; }
.callBtn:not(:disabled):active { transform: scale(0.94); }
.callBtn:disabled { opacity: 0.25; color: var(--color-text-tertiary); cursor: not-allowed; }
.lineType { font-size: var(--fs-sm); color: var(--color-text-tertiary); }
.lineNum { font-family: var(--font-mono); font-variant-numeric: tabular-nums; font-size: var(--fs-sm); color: var(--color-text-primary); }
.lineNumOn { font-weight: var(--fw-semibold); }
.lineStatus { font-size: var(--fs-sm); color: var(--color-text-tertiary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lineStatus.done { color: var(--color-text-secondary); }

/* The live cell replaces status+trailing on the dialled row. */
.lineLive { grid-column: 3 / -1; display: inline-flex; align-items: center; gap: var(--space-2); font-size: var(--fs-sm); font-weight: var(--fw-semibold); font-variant-numeric: tabular-nums; color: var(--color-text-secondary); }
.lineLive.on { color: var(--color-accent); }
.lineLive.degraded { color: var(--hue-amber); }
.liveFrom { font-size: var(--fs-xs); font-family: var(--font-mono); font-weight: var(--fw-regular); color: var(--color-text-tertiary); }
.machineTag { display: inline-flex; align-items: center; gap: 4px; padding: 1px 7px; border-radius: var(--radius-full); background: var(--color-bg-app); font-size: 10px; font-weight: var(--fw-semibold); letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-text-tertiary); }
.pulse { width: 8px; height: 8px; border-radius: 999px; background: currentColor; animation: pulse 1.1s var(--ease-out) infinite; }
.lineLive.on .pulse { animation-duration: 1.4s; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
</style>
